import React, { useRef, useState, useEffect, useCallback } from 'react';
import { View, TouchableOpacity, ScrollView, StyleProp, ViewStyle } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { createStyleSheet, UnistylesTheme, useStyles } from 'react-native-unistyles';
import { translate } from '../services/translate';
import MyAppText from './MyAppText';
import Tooltip from './Tooltip';
import Divider from './Divider';
import useDebounce from './hooks/useDebounce';
import Toast from 'react-native-toast-message';
import { faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons';

type FileType = 'image/png' | 'image/jpg' | 'image/jpeg' | 'image/gif' | 'image/webp' | 'application/pdf' | 'text/plain' | 'text/csv';

interface ExpandableCommentInputProps {
    value: string;
    onChangeText: (value: string) => void;
    files: File[];
    onFilesChange: (files: File[]) => void;
    onSubmit?: () => void;
    disabled?: boolean;
    placeholder?: string;
    label?: string;
    textAreaMaxHeight?: number;
    textAreaMinHeight?: number;
    required?: boolean;
    containerStyle?: StyleProp<ViewStyle>;
    maxFiles?: number;
    submitButtonText?: string;
    FilePreviewComponent: React.ComponentType<{
        file: File;
        onRemove: () => void;
    }>;
    acceptedFileTypes?: FileType[];
    maxFileSize?: number; // MB per file
    maxTotalSize?: number; // MB total
    downloadableFiles?: boolean;
    textAreaSize?: number;
}

interface FileSectionProps {
    files: File[];
    FilePreviewComponent: React.ComponentType<{
        file: File;
        onRemove: () => void;
        downloadable?: boolean;
        onDownload?: () => void;
    }>;
    onRemoveFile: (index: number) => void;
    theme: UnistylesTheme;
    downloadableFiles: boolean;
}

export default function ExpandableCommentInput({
    value,
    onChangeText,
    files,
    onFilesChange,
    onSubmit,
    disabled = false,
    placeholder,
    label,
    required = true,
    containerStyle,
    maxFiles,
    FilePreviewComponent,
    textAreaMaxHeight = 200,
    textAreaMinHeight = 44,
    acceptedFileTypes,
    maxFileSize,
    maxTotalSize,
    downloadableFiles,
}: ExpandableCommentInputProps) {
    const { styles, theme } = useStyles(styleSheet);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const [tooltipHeight, setTooltipHeight] = useState<number>(0);

    const [isDragOver, setIsDragOver] = useState(false);
    const dragCounterRef = useRef(0);
    const [message, setMessage] = useState<{ text: string; type: 'error' | 'warning'; } | null>(null);

    const [localTextValue, setLocalTextValue] = useState(value);

    const debouncedTextValue = useDebounce(localTextValue, 500);

    const FileSection = React.memo<FileSectionProps>(({ files, FilePreviewComponent, onRemoveFile, theme, downloadableFiles }) => {

        if (files.length === 0) {
            return null;
        }

        async function downloadFile(file: File) {
            try {
                const blobUrl = URL.createObjectURL(file);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = file.name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);
            } catch (error) {
                console.error('Download failed:', error);
                Toast.show({
                    type: 'sentinelxError',
                    text1: translate('unexpectedError'),
                });
            }
        }

        return (
            <>
                <View style={{ flex: 1, justifyContent: 'center' }}>
                    <Divider
                        color={theme.colors.neutral['300']}
                        thickness={1}
                        style={{ width: '98%' }}
                    />
                </View>
                <View
                    style={styles.filePreview}
                >
                    <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={{ gap: 8 }}
                    >
                        {files.map((file, index) => (
                            <FilePreviewComponent
                                key={`${file.name}-${file.size}-${file.lastModified}`}
                                file={file}
                                onRemove={() => onRemoveFile(index)}
                                downloadable={downloadableFiles}
                                onDownload={() => downloadFile(file)}
                            />
                        ))}
                    </ScrollView>
                </View>
            </>
        );
    });

    FileSection.displayName = 'FileSection';

    useEffect(() => {
        onChangeText(debouncedTextValue);
    }, [debouncedTextValue]);

    const tooltipContent = maxFiles
        ? files.length >= maxFiles
            ? translate('replace_files')
            : `${translate('chooseFile')} (${files.length}/${maxFiles})`
        : translate('chooseFile');

    useEffect(() => {
        if (tooltipRef.current) {
            setTooltipHeight(tooltipRef.current.offsetHeight);
        }
    }, [tooltipRef.current, tooltipContent]);

    const autoResize = () => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = `${textAreaMinHeight}px`;
            const newHeight = Math.min(textarea.scrollHeight, textAreaMaxHeight);
            textarea.style.height = `${Math.max(textAreaMinHeight, newHeight)}px`;
        }
    };

    useEffect(() => {
        autoResize();
    }, [localTextValue]);

    useEffect(() => {
        if (value != localTextValue) {
            setLocalTextValue(value);
        }
    }, [value]);

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => setMessage(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    function handleFiles(newFiles: File[]) {
        if (newFiles.length === 0) {
            return;
        }

        setMessage(null);

        if (acceptedFileTypes && acceptedFileTypes.length > 0) {
            const invalidFiles = newFiles.filter(file => !acceptedFileTypes.includes(file.type as FileType));
            if (invalidFiles.length > 0) {
                setMessage({ text: translate('invalid_file_type'), type: 'error' });
                return;
            }
        }

        if (maxFileSize) {
            const oversizedFiles = newFiles.filter(file => file.size > maxFileSize * 1024 * 1024);
            if (oversizedFiles.length > 0) {
                setMessage({
                    text: translate('file_size_exceeded', { maxSize: maxFileSize }),
                    type: 'error'
                });
                return;
            }
        }

        if (maxTotalSize) {
            const currentTotalSize = files.reduce((sum, file) => sum + file.size, 0);
            const newTotalSize = newFiles.reduce((sum, file) => sum + file.size, 0);
            const totalSize = currentTotalSize + newTotalSize;

            if (totalSize > maxTotalSize * 1024 * 1024) {
                setMessage({
                    text: translate('total_size_exceeded', { maxSize: maxTotalSize }),
                    type: 'error'
                });
                return;
            }
        }

        if (maxFiles !== undefined) {
            const availableSlots = maxFiles - files.length;

            if (availableSlots <= 0) {
                const filesToReplace = newFiles.slice(0, maxFiles);
                onFilesChange(filesToReplace);

                if (newFiles.length > maxFiles) {
                    setMessage({
                        text: translate('files_replaced', {
                            count: maxFiles,
                            total: newFiles.length
                        }),
                        type: 'warning'
                    });
                } else {
                    setMessage({ text: translate('files_replaced_all'), type: 'warning' });
                }
            } else {
                const filesToAdd = newFiles.slice(0, availableSlots);
                onFilesChange([...files, ...filesToAdd]);

                if (newFiles.length > availableSlots) {
                    setMessage({
                        text: translate('some_files_added', {
                            added: filesToAdd.length,
                            max: maxFiles,
                            remaining: newFiles.length - availableSlots
                        }),
                        type: 'warning'
                    });
                }
            }
        } else {
            onFilesChange([...files, ...newFiles]);
        }
    }

    function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
        if (!e.target.files) {
            return;
        }
        handleFiles(Array.from(e.target.files));
        e.target.value = '';
    }

    const removeFile = useCallback((index: number) => {
        onFilesChange(files.filter((_, i) => i !== index));
        setMessage(null);
    }, [onFilesChange]);

    function handleDragEnter(e: React.DragEvent) {
        e.preventDefault();
        e.stopPropagation();

        if (!disabled) {
            dragCounterRef.current++;
            if (dragCounterRef.current === 1) {
                setIsDragOver(true);
            }
        }
    }

    function handleDragOver(e: React.DragEvent) {
        e.preventDefault();
        e.stopPropagation();
    }

    function handleDragLeave(e: React.DragEvent) {
        e.preventDefault();
        e.stopPropagation();

        if (!disabled) {
            dragCounterRef.current--;
            if (dragCounterRef.current === 0) {
                setIsDragOver(false);
            }
        }
    }

    function handleDrop(e: React.DragEvent) {
        e.preventDefault();
        e.stopPropagation();

        dragCounterRef.current = 0;
        setIsDragOver(false);

        if (disabled) {
            return;
        }

        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles && droppedFiles.length > 0) {
            handleFiles(Array.from(droppedFiles));
        }
    }

    const messageColor = message?.type === 'error'
        ? theme.colors.errors['300']
        : theme.colors.warnings['300'];


    return (
        <View style={[{ gap: 10 }, containerStyle]}>
            {label && (
                <View style={[{ flexDirection: 'row', columnGap: 5 }]}>
                    <MyAppText>{label}</MyAppText>
                    {required && (
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>
                            (*)
                        </MyAppText>
                    )}
                </View>
            )}

            <div
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={styles.dragAndDropBase(isDragOver)}
            >
                <View
                    style={styles.fileDrop}
                >
                    <Tooltip
                        ref={tooltipRef}
                        useSimpleBox
                        tooltipDirection="top"
                        tooltipContent={tooltipContent}
                        style={styles.tooltipFile(tooltipHeight)}
                    >
                        <TouchableOpacity
                            onPress={() => fileInputRef.current?.click()}
                            disabled={disabled}
                            style={styles.addFileButton(disabled)}
                        >
                            <FontAwesomeIcon
                                icon={faPlusCircle}
                                color={theme.colors.chat.iconColor}
                                fontSize={20}
                            />
                        </TouchableOpacity>
                    </Tooltip>

                    <textarea
                        ref={textareaRef}
                        value={localTextValue}
                        onChange={(e) => {
                            setLocalTextValue(e.target.value);
                            autoResize();
                        }}
                        onInput={autoResize}
                        placeholder={placeholder || translate('comment')}
                        disabled={disabled}
                        style={{ ...styles.textArea, outline: 'none' }}
                    />

                    {onSubmit && (
                        <TouchableOpacity
                            onPress={onSubmit}
                            disabled={disabled || (required && (!localTextValue || localTextValue.trim() === ''))}
                            style={{
                                padding: 8,
                                borderRadius: 8,
                                justifyContent: 'center',
                                alignItems: 'center',
                                opacity: (disabled || (required && (!localTextValue || localTextValue.trim() === ''))) ? 0.5 : 1,
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faPaperPlaneTop as IconDefinition}
                                color={theme.colors.chat.iconColor}
                                fontSize={20}
                            />
                        </TouchableOpacity>
                    )}
                </View>

                {isDragOver && (
                    <div
                        style={styles.textAreaDrag}
                    >
                        <MyAppText
                            style={{
                                fontSize: 16,
                                fontWeight: '600',
                                color: theme.colors.primary['500'],
                            }}
                        >
                            {maxFiles && files.length >= maxFiles
                                ? translate('drop_to_replace')
                                : translate('drop_files_here')
                            }
                        </MyAppText>
                    </div>
                )}
                <FileSection
                    files={files}
                    FilePreviewComponent={FilePreviewComponent}
                    onRemoveFile={removeFile}
                    theme={theme}
                    downloadableFiles={downloadableFiles || false}
                />
            </div>

            {
                message && (
                    <MyAppText
                        style={{
                            color: messageColor,
                            fontSize: 10,
                        }}
                    >
                        {message.text}
                    </MyAppText>
                )
            }

            <input
                ref={fileInputRef}
                type="file"
                multiple={maxFiles === undefined || maxFiles > 1}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                disabled={disabled}
            />
        </View >
    );
}

const styleSheet = createStyleSheet((theme) => ({
    filePreview: {
        paddingHorizontal: 12,
        paddingBottom: 12,
        paddingTop: 12,
    },
    dragAndDropBase: (isDragOver: boolean) => {
        return {
            borderRadius: 4,
            overflow: 'visible',
            position: 'relative',
            borderWidth: isDragOver ? 2 : 1,
            borderStyle: isDragOver ? 'dashed' : 'solid',
            backgroundColor: isDragOver ? theme.colors.primary['200'] : theme.colors.field.background,
            borderColor: isDragOver ? theme.colors.primary['500'] : theme.colors.dataTable.border,
            outline: 'none'
        };
    },
    fileDrop: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        paddingHorizontal: 12,
        paddingVertical: 2,
        gap: 16,
        overflow: 'visible'
    },
    tooltipFile: (tooltipHeight: number) => {
        return {
            width: 'auto',
            minWidth: 125,
            color: theme.colors.white['500'],
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 16,
            paddingRight: 16,
            borderRadius: 12,
            justifyContent: 'center',
            zIndex: 9999,
            fontSize: 12,
            fontWeight: '500',
            top: -tooltipHeight,
            left: 0,
            background: theme.colors.neutral['500'],
        };
    },
    addFileButton: (disabled: boolean) => {
        return {
            padding: 8,
            borderRadius: 8,
            justifyContent: 'center',
            alignItems: 'center',
            opacity: disabled ? 0.5 : 1,
        };
    },
    textArea: {
        flex: 1,
        fontSize: 14,
        fontFamily: 'Open Sans',
        color: theme.colors.textColor,
        backgroundColor: 'transparent',
        border: 'none',
        resize: 'none' as const,
        overflow: 'hidden',
        borderRadius: 4,
        padding: 0,
    },
    textAreaDrag: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: theme.colors.primary[500],
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        pointerEvents: 'none',
        zIndex: 10,
        outline: 'none',
    }
}));
